// @flow
import type { ChildrenArray, Element, Node } from 'react';
import React, { useLayoutEffect } from 'react';
import { useSetRecoilState } from 'recoil';

import {
  types as t,
  usePropsValidation,
} from './validation/runtime-checks/index.js';
import { QuestionsGroup } from './Questions.js';
import packageInfo from './package.json';
import { SLDConceptSummary } from './solidify-lesson/SLDConceptSummary.js';
import { SLDWorkedExamples } from './solidify-lesson/SolidifyWorkedExample.js';
import { STNAdditionalExamples } from './solidify-lesson/STNAdditionalExamples.js';
import { TSOLessonNarrative } from './teacher-subtopic-overview/TSOLessonNarrative.js';
import { TSOLearningTargetsList } from './teacher-subtopic-overview/TSOLearningTargetsList.js';
import { TSOKeyVocabularyList } from './teacher-subtopic-overview/TSOKeyVocabularyList.js';
import { TSOEssentialUnderstanding } from './teacher-subtopic-overview/TSOEssentialUnderstanding.js';
import { TSOStandardsAndBenchmarksSection } from './teacher-subtopic-overview/TSOStandardsAndBenchmarksSection.js';
import { TSOStandardsSection } from './teacher-subtopic-overview/TSOStandardsSection.js';
import { STNInstructionalSupport } from './solidify-lesson/STNInstructionalSupport.js';
import TOVBigIdeasSection from './topic-info/TOVBigIdeasSection.js';
import { TOVFoundationalKnowledgeList } from './topic-info/TOVFoundationalKnowledgeList.js';
import TOVNarrative from './topic-info/TOVNarrative.js';
import { TOVFutureConnectionsList } from './topic-info/TOVFutureConnectionsList.js';
import { EANIntroduction } from './engage-activity-note/EANIntroduction.js';
import { EANClassroomGuide } from './engage-activity-note/EANClassroomGuide.js';
import GlossaryTerm from './glossary/glossaryTerm.js';
import { SSOEssentialQuestionsList } from './subtopic-overview/SSOEssentialQuestionsList.js';
import { SSOLearningTargetsList } from './subtopic-overview/SSOLearningTargetsList.js';
import { SSOKeyVocabularyList } from './subtopic-overview/SSOKeyVocabularyList.js';
import {
  languages,
  type LanguageType,
} from './validation/static-analysis/index.js';
import { ContentContext } from './JsxContent.js';
const componentsVersion = packageInfo.version;
import { languageState } from './languages.js';
import { InternalNavigationGenerator as RSLInternalNavigationGenerator } from './rsl-docs/retrofitted-student-lesson/idea-navigation.js';
import { InternalNavigationGenerator as RSLTNInternalNavigationGenerator } from './rsl-docs/retrofitted-student-lesson-teacher-note/idea-navigation.js';
import { InternalTabNavigationGenerator } from './inv-docs/investigation/section-navigation.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import {
  RSLIntroduction,
  RSLIdeaSection,
} from './rsl-docs/retrofitted-student-lesson/structural-components.js';
import {
  RSLTNIntroduction,
  RSLTNIdeaSection,
} from './rsl-docs/retrofitted-student-lesson-teacher-note/structural-components.js';
import {
  INVOverview,
  INVActivitySection,
  INVReflection,
} from './inv-docs/investigation/structural-components.js';
import { INVTNOverview } from './inv-docs/investigation-teacher-note/INVTNOverview.js';
import { INVTNActivityGuide } from './inv-docs/investigation-teacher-note/INVTNActivityGuide.js';
import { INVTNReflect } from './inv-docs/investigation-teacher-note/INVTNReflect.js';
import { childrenArrayMatcher } from './childrenArrayMatcher.js';
import { productColorSunflower } from './designSystem.js';
import {
  numWorkedQuestions,
  numTeacherLedExamples,
} from './rsl-docs/numWorkedQuestions.js';

// Documents.js houses all the *static* document types, since they all have similar characteristics.
// Notably, our only highly interactive document — EngageActivity — does not live here.
//
// It probably makes sense to rename this StaticDocuments.js to reinforce this distinction.

function Document({ children }: {| children: Node |}) {
  const { baseFontSize, textColor, fontFamily } =
    React.useContext(ContentContext);
  return (
    <div
      style={{
        width: 716, // Grid system is based upon this
        fontFamily,
        // TODO probs shouldn't rely on inheritance for color as that breaks component encapsulation.
        color: textColor,
        lineHeight: 1.3,
        fontSize: baseFontSize,
      }}
    >
      {children}
    </div>
  );
}

function MobileEnabledDocument({ children }: {| children: Node |}) {
  const { baseFontSize, textColor, fontFamily } =
    React.useContext(ContentContext);
  return (
    <div
      style={{
        // TODO probably not what we want. We don't really want responsive
        // docs. We want fixed-width for "not phone", then a phone-optimised
        // layout for the phone app.
        maxWidth: 716,
        fontFamily,
        // TODO probs shouldn't rely on inheritance for color as that breaks component encapsulation.
        color: textColor,
        lineHeight: 1.3,
        fontSize: baseFontSize,
      }}
    >
      {children}
    </div>
  );
}

export function Investigation(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<
    | React$Element<typeof INVOverview>
    | React$Element<typeof INVActivitySection>
    | React$Element<typeof INVReflection>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, Investigation);
  useUpdateDocumentLanguage(failedValidation, props.language);

  if (failedValidation) return null;

  return (
    <MobileEnabledDocument>
      {/* Overwriting inherited styles from Document. Gross */}
      <div style={{ color: productColorSunflower.grey }}>
        <InternalTabNavigationGenerator>
          {props.children}
        </InternalTabNavigationGenerator>
      </div>
    </MobileEnabledDocument>
  );
}
Investigation.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(INVOverview) },
    { min: 1, max: Infinity, elementType: t.elementType(INVActivitySection) },
    { min: 0, max: 1, elementType: t.elementType(INVReflection) },
  ),
};

export function InvestigationTeacherNote(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  children: ChildrenArray<
    | React$Element<typeof INVTNOverview>
    | React$Element<typeof INVTNActivityGuide>
    | React$Element<typeof INVTNReflect>,
  >,
  ...CompilerInjectedProps,
|}) {
  let activityIndex = 0;
  const indexedChildren = React.Children.map(props.children, (child) => {
    if (child.type.name === INVTNActivityGuide.name) {
      // $FlowIssue bad cloneElement signature
      return React.cloneElement(child, { _index: activityIndex++ });
    } else return child;
  });
  return (
    <Document>
      <div style={{ color: productColorSunflower.grey }}>{indexedChildren}</div>
    </Document>
  );
}
InvestigationTeacherNote.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(INVTNOverview) },
    { min: 1, max: Infinity, elementType: t.elementType(INVTNActivityGuide) },
    { min: 0, max: 1, elementType: t.elementType(INVTNReflect) },
  ),
};

export function RetrofittedStudentLesson(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<
    Element<typeof RSLIntroduction> | Element<typeof RSLIdeaSection>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RetrofittedStudentLesson,
  );
  useUpdateDocumentLanguage(failedValidation, props.language);

  if (failedValidation) return null;

  let _numPrecedingQuestions = 0;
  const mappedChildren = React.Children.map(props.children, (child) => {
    if (child.type !== RSLIdeaSection) return child;
    const _child: React$Element<typeof RSLIdeaSection> = (child: any);
    const clonedChild = React.cloneElement(_child, { _numPrecedingQuestions });
    _numPrecedingQuestions += numWorkedQuestions(_child);
    return clonedChild;
  });

  return (
    <MobileEnabledDocument>
      {/* Overwriting inherited styles from Document. Gross */}
      <div style={{ color: productColorSunflower.grey }}>
        <RSLInternalNavigationGenerator
          performValidation={props._performValidation}
        >
          {mappedChildren}
        </RSLInternalNavigationGenerator>
      </div>
    </MobileEnabledDocument>
  );
}
RetrofittedStudentLesson.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(RSLIntroduction) },
    { min: 1, max: Infinity, elementType: t.elementType(RSLIdeaSection) },
  ),
};

export function RetrofittedStudentLessonTeacherNote(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  children: ChildrenArray<
    Element<typeof RSLTNIntroduction> | Element<typeof RSLTNIdeaSection>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RetrofittedStudentLessonTeacherNote,
  );

  if (failedValidation) return null;

  let _numPrecedingTeacherLedExamples = 0;
  const mappedChildren = React.Children.map(props.children, (child) => {
    if (child.type !== RSLTNIdeaSection) return child;
    const _child: React$Element<typeof RSLTNIdeaSection> = (child: any);
    const clonedChild = React.cloneElement(_child, {
      _numPrecedingTeacherLedExamples,
    });
    _numPrecedingTeacherLedExamples += numTeacherLedExamples(_child);
    return clonedChild;
  });

  return (
    // Overwriting inherited styles from Document. Gross
    <div style={{ color: productColorSunflower.grey }}>
      <RSLTNInternalNavigationGenerator
        performValidation={props._performValidation}
      >
        {mappedChildren}
      </RSLTNInternalNavigationGenerator>
    </div>
  );
}
RetrofittedStudentLessonTeacherNote.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(RSLTNIntroduction) },
    { min: 1, max: Infinity, elementType: t.elementType(RSLTNIdeaSection) },
  ),
};

export function Worksheet(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<Element<typeof QuestionsGroup>>,
|}) {
  const { failedValidation } = usePropsValidation(props, Worksheet);
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
Worksheet.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: t.childrenArray(t.elementType(QuestionsGroup)),
};

export function AnswerKey(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<Element<typeof QuestionsGroup>>,
|}) {
  const { failedValidation } = usePropsValidation(props, AnswerKey);
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
AnswerKey.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: t.childrenArray(t.elementType(QuestionsGroup)),
};

export function Playground(props: {| version: string, children: Node |}) {
  const { failedValidation } = usePropsValidation(props, Playground);
  if (failedValidation) return null;

  return <Document>{props.children}</Document>;
}
Playground.spec = {
  version: versionChecker,
  children: t.node,
};

export function PrimaryTeacherNote(props: {|
  version: string,
  children: Node,
|}) {
  const { failedValidation } = usePropsValidation(props, PrimaryTeacherNote);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
PrimaryTeacherNote.spec = {
  version: versionChecker,
  children: t.node,
};

export function SolidifyLesson(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: Node,
|}) {
  const { failedValidation } = usePropsValidation(props, SolidifyLesson);
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
SolidifyLesson.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: t.exactOrderChildrenArray(
    t.elementType(SLDConceptSummary),
    t.elementType(SLDWorkedExamples),
  ),
};

export function SolidifyTeacherNote(props: {| children: Node |}) {
  const { failedValidation } = usePropsValidation(props, SolidifyTeacherNote);
  if (failedValidation) return null;

  const children = React.Children.map(props.children, (child, index) => {
    return React.cloneElement(child, { _index: index });
  });

  return <Document>{children}</Document>;
}
SolidifyTeacherNote.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: t.exactOrderChildrenArray(
    t.elementType(STNInstructionalSupport),
    t.elementType(STNAdditionalExamples),
  ),
};

export function TeacherSubtopicOverview(props: {|
  children: $ReadOnlyArray<
    | Element<typeof TSOLearningTargetsList>
    | Element<typeof TSOKeyVocabularyList>
    | Element<typeof TSOEssentialUnderstanding>
    | Element<typeof TSOStandardsAndBenchmarksSection>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TeacherSubtopicOverview,
  );
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
TeacherSubtopicOverview.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: t.exactOrderChildrenArray(
    t.optional(t.elementType(TSOLessonNarrative)),
    t.elementType(TSOLearningTargetsList),
    t.elementType(TSOKeyVocabularyList),
    t.elementType(TSOEssentialUnderstanding),
    // TODO we can't currently express the desired constraint here which is:
    // Precisely one of these two components must exist.
    t.optional(t.elementType(TSOStandardsAndBenchmarksSection)),
    t.optional(t.elementType(TSOStandardsSection)),
  ),
};

export function TopicOverview(props: {| children: Node |}) {
  const { failedValidation } = usePropsValidation(props, TopicOverview);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
TopicOverview.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: t.exactOrderChildrenArray(
    t.optional(t.elementType(TOVFoundationalKnowledgeList)),
    t.elementType(TOVBigIdeasSection),
    t.optional(t.elementType(TOVNarrative)),
    t.optional(t.elementType(TOVFutureConnectionsList)),
  ),
};

export function TopicAssessment(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<Element<typeof QuestionsGroup>>,
|}) {
  const { failedValidation } = usePropsValidation(props, TopicAssessment);
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
TopicAssessment.spec = Worksheet.spec;

export function TopicAnswerKey(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: ChildrenArray<Element<typeof QuestionsGroup>>,
|}) {
  const { failedValidation } = usePropsValidation(props, TopicAnswerKey);
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
TopicAnswerKey.spec = AnswerKey.spec;

export function EngageActivityNote(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  children: [
    Element<typeof EANIntroduction>,
    Element<typeof EANClassroomGuide>,
  ],
|}) {
  const { failedValidation } = usePropsValidation(props, EngageActivityNote);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}
EngageActivityNote.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: t.exactOrderChildrenArray(
    t.elementType(EANIntroduction),
    t.elementType(EANClassroomGuide),
  ),
};

export function Glossary(props: {|
  version: string,
  paperSize: 'A4' | 'Letter',
  children: ChildrenArray<Element<typeof GlossaryTerm>>,
|}) {
  const { failedValidation } = usePropsValidation(props, Glossary);
  if (failedValidation) return null;

  return <Document>{props.children}</Document>;
}

Glossary.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  children: t.childrenArray(t.elementType(GlossaryTerm)),
};

export function StudentSubtopicOverview(props: {
  version: string,
  paperSize: 'A4' | 'Letter',
  language: LanguageType,
  children: $ReadOnlyArray<
    | Element<typeof SSOEssentialQuestionsList>
    | Element<typeof SSOLearningTargetsList>
    | Element<typeof SSOKeyVocabularyList>,
  >,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    StudentSubtopicOverview,
  );
  useUpdateDocumentLanguage(failedValidation, props.language);
  if (failedValidation) return null;
  return <Document>{props.children}</Document>;
}

StudentSubtopicOverview.spec = {
  version: versionChecker,
  paperSize: t.unionOfValues('A4', 'Letter'),
  language: t.unionOfValues(...languages),
  children: t.exactOrderChildrenArray(
    t.elementType(SSOEssentialQuestionsList),
    t.elementType(SSOLearningTargetsList),
    t.elementType(SSOKeyVocabularyList),
  ),
};

// TODO having to define these outside is a bit clumsy.
// This is required in order to add the description property to the
// function.
//
// One option would be to have a helper function for custom ones:
//  customChecker({
//    checker(props, propName) {},
//    description: '',
//  })
function versionChecker(props, propName) {
  const version = props[propName];
  if (typeof version === 'string') {
    const failure = t.string(props, propName);
    if (failure) return failure;
    const parsedVersion = parseSemver(version);
    const parsedComponentsVersion = parseSemver(componentsVersion);

    if (parsedVersion === null) {
      return {
        typeDescription: versionChecker.description,
        reason: `must be a valid semver string:\n "<major>.<minor>.<patch>"`,
      };
    }

    if (parsedVersion.major !== parsedComponentsVersion?.major) {
      return {
        typeDescription: versionChecker.description,
        reason: `must be "${componentsVersion}", not "${version}". This indicates that your document was authored using now-obsolete definitions of components. You will need to manually migrate this document to use the new definitions and re-check the entire document to ensure it is rendering correctly. You will need to look at all the breaking changes that have occurred between the old version: "${version}" and the current version "${componentsVersion}" and ensure all changes that impact this document have been made.`,
      };
    }
  }
  return null;
}
versionChecker.description = `Valid version string`;

// <major>.<minor>.<path>
const semverRe = /(\d+)\.(\d+)\.(\d+)/;
function parseSemver(str: string) {
  const matches = str.match(semverRe);
  if (matches === null || matches.length !== 4) {
    return null;
  }

  return {
    major: matches[1],
    minor: matches[2],
    patch: matches[3],
  };
}

function useUpdateDocumentLanguage(failedValidation, language) {
  const setLanguage = useSetRecoilState(languageState);
  useLayoutEffect(() => {
    // It is important to not attempt to setLanguage when the props
    // interface contract is violated, as this can mean that the
    // language prop is not a valid value for our atom.
    if (failedValidation) return;
    setLanguage(language);
  }, [failedValidation, language, setLanguage]);
}
